<script setup lang="ts">
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { voucherErrorParser, vouchersApiService } from '@nsftx/seven-client-retail-vue-sgp'
import { useDeviceManagementStore } from '@nsftx/seven-client-retail-vue-dm'
import { logService } from '@nsftx/seven-client-retail-utils'
import { NXGDSButton, NXGDSButtonVariants, NXGDSButtonSizes } from '@nsftx/seven-nxg-design-system'
import { useWalletStore } from '@/stores/wallet/walletStore'
import { getWallets } from '@/modules/wallet/walletService'
import errorParser from '@/utils/errorParser'
import { printVoucher } from '@/modules/voucher/voucherSSBTService'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import i18n from '@/plugins/translationsPlugin'

const LOG_PREFIX = '[PrintVoucherHeaderItem]'
const { t } = i18n.global

const { appData } = storeToRefs(useDeviceManagementStore())
const { activeWallet } = storeToRefs(useWalletStore())
const { showNotification } = useNotificationsStore()

const isLoading = ref(false)
const showIssueVoucherAction = computed(() => Number(activeWallet.value?.balance) > 0)

const issueVoucher = async () => {
  try {
    isLoading.value = true
    const res = await vouchersApiService.issueVoucher(
      appData.value!.betshop.companyUuid,
      Number(activeWallet.value!.balance),
      activeWallet.value!.currency
    )

    logService.info(`${LOG_PREFIX} Voucher issued successfully`, {
      code: 'ST_VOUCHER_ISSUE_SUCCESS',
      voucher_code: res.data.barcode
    })

    getWallets('ui') // update wallet balance after voucher issuance
      .catch((err) => {
        logService.error(
          `${LOG_PREFIX} Failed to get wallets for the punter after voucher issuance.`,
          {
            code: 'ST_GET_WALLETS_FAILED',
            ...errorParser.parseUpstream(err)
          }
        )
      })
    await printVoucher(res.data, 'payin')
  } catch (err) {
    logService.error(`${LOG_PREFIX} Failed to issue voucher`, {
      ...voucherErrorParser.parseUpstream(err),
      code: 'ST_VOUCHER_ISSUE_ERROR'
    })
    showNotification({
      variant: 'error',
      text: [t('voucher_issue_failed')]
    })
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <NXGDSButton
    v-if="showIssueVoucherAction"
    prepend-icon="vouchers"
    :variant="NXGDSButtonVariants.invertedBrand"
    :size="NXGDSButtonSizes.big"
    :loading="isLoading"
    :disabled="isLoading"
    @click="issueVoucher"
  >
    <span class="st-print-voucher__text">
      {{ $t('voucher_print') }}
    </span>
  </NXGDSButton>
</template>

<style lang="scss" scoped>
.st-print-voucher__text {
  text-transform: capitalize;
}
</style>
